/**
 * `/agents/qa` — Browser QA (RO-3-02; docs/qa/AGENT-BROWSER-QA-PRD.md §5.9).
 * Port of `excellent/app/agents/qa/{page,_runs-table,_suites-panel}.tsx`. This
 * page reads the SAME artifact bundles an agent's `browser_qa run` writes to
 * `~/.excellent/qa-runs/`, now through the `qa` contract
 * (`packages/ui/src/api/contracts/qa.ts` — already existed; RO-3-02 is what
 * mounts it on the Hono host, `server/routes/agent-qa.ts`) instead of a direct
 * `@excellent/core/browser` read inside an async Server Component.
 *
 * `/agents/qa/$runId` (the run detail — steps, console, network, screenshots)
 * is the REWRITE half of this section (182 LOC of genuine server-JSX
 * composition) and is deferred to RO-3-13; the run ledger below still links
 * to it (`rowHref`), so those links 404 until it lands — the honest port of
 * "the row opens the run detail," not a claim that the detail page exists yet.
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  StatCard,
  Button,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { qa, type QaRunSummaryDTO, type QaSuiteDTO } from "@excellent/ui/api/contracts/qa";
import { rootRoute } from "./__root";

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function QaRunsTable({ runs }: { runs: QaRunSummaryDTO[] }) {
  const columns: DataTableColumn<QaRunSummaryDTO>[] = [
    {
      key: "ok",
      header: "Result",
      render: (r) => (
        <IntentBadge intent={r.ok ? "success" : "danger"}>{r.ok ? "Pass" : "Fail"}</IntentBadge>
      ),
    },
    { key: "label", header: "Suite", render: (r) => <span className="text-foreground">{r.label}</span> },
    {
      key: "steps",
      header: "Steps",
      render: (r) => (
        <span className="tabular-nums">{r.ok ? r.steps : `${r.failedAt ?? "?"} of ${r.steps}`}</span>
      ),
    },
    {
      key: "ms",
      header: "Duration",
      render: (r) => <span className="tabular-nums">{formatDuration(r.ms)}</span>,
    },
    {
      key: "reason",
      header: "Failure",
      render: (r) =>
        r.reason ? (
          <span className="type-subtext text-muted-foreground line-clamp-1" title={r.reason}>
            {r.reason}
          </span>
        ) : (
          <span className="type-subtext text-muted-foreground">—</span>
        ),
    },
    {
      key: "startedAt",
      header: "When",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {formatRelativeTime(new Date(r.startedAt).toISOString())}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      rows={runs}
      columns={columns}
      rowKey={(r) => r.runId}
      rowHref={(r) => `/agents/qa/${encodeURIComponent(r.runId)}`}
      wrapInCard={false}
    />
  );
}

function QaSuitesPanel({ suites }: { suites: QaSuiteDTO[] }) {
  const navigate = agentsQaRoute.useNavigate();
  const [running, setRunning] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  async function run(slug: string) {
    setRunning(slug);
    setError(null);
    try {
      const res = await apiCall(qa.suiteRun, { body: { slug } });
      if (res.runId) void navigate({ to: `/agents/qa/${encodeURIComponent(res.runId)}` });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Suites</CardTitle>
      </CardHeader>
      <CardContent>
        {suites.length === 0 ? (
          <p className="type-body text-muted-foreground">
            No saved suites. A suite is a JSON file in <code>~/.excellent/qa-suites/</code>{" "}
            holding <code>{"{ slug, name, steps }"}</code>, where steps use the same vocabulary as{" "}
            <code>browser_qa</code> <code>op=run</code>. Agents can write them directly.
          </p>
        ) : (
          <div className="divide-y">
            {suites.map((s) => (
              <div key={s.slug} className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="type-body text-foreground">{s.name}</span>
                    {s.destructive ? <IntentBadge intent="warning">Destructive</IntentBadge> : null}
                  </div>
                  <p className="type-subtext text-muted-foreground line-clamp-1">
                    {s.description ?? `${s.steps.length} steps`} · updated{" "}
                    {formatRelativeTime(new Date(s.updatedAt).toISOString())}
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => run(s.slug)}
                  disabled={running !== null || s.destructive}
                  title={s.destructive ? "Destructive suites run from the CLI with --isolated" : undefined}
                >
                  {running === s.slug ? "Running…" : "Run"}
                </Button>
              </div>
            ))}
          </div>
        )}
        {error ? <p className="mt-3 type-subtext text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}

function AgentsQaPage() {
  const { data: runsData } = useApi(qa.runsList, { query: { limit: 200 } });
  const { data: suitesData } = useApi(qa.suitesList, {});
  const runs = runsData?.runs ?? [];
  const suites = suitesData?.suites ?? [];

  const failed = runs.filter((r) => !r.ok).length;
  const lastRun = runs[0] ?? null;

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title="Browser QA"
        description="Real-browser tests of this app, driven by agents through the browser_qa tool. Every run here is the same evidence bundle an agent produced — steps, screenshots, console and network."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard title="Runs recorded" value={String(runs.length)} />
        <StatCard
          title="Failing"
          value={String(failed)}
          leadingDot={failed > 0 ? "danger" : "success"}
        />
        <StatCard title="Suites" value={String(suites.length)} />
      </div>

      <QaSuitesPanel suites={suites} />

      <Card>
        <CardHeader>
          <CardTitle>Runs</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="type-body text-muted-foreground">
              No runs yet. An agent records one with <code>browser_qa</code> <code>op=run</code>{" "}
              and a label, or from a terminal: <code>excellent-mcp qa run --steps &apos;[…]&apos;</code>.
              Runs without a label are not archived.
            </p>
          ) : (
            <QaRunsTable runs={runs} />
          )}
        </CardContent>
      </Card>

      {lastRun && !lastRun.ok ? (
        <p className="type-subtext text-muted-foreground">
          Most recent run failed at step {lastRun.failedAt}: {lastRun.reason}
        </p>
      ) : null}
    </div>
  );
}

export const agentsQaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/qa",
  component: AgentsQaPage,
});
